import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { SettingsSection } from "./SettingsSection";
import type {
  ClientDetails,
  CreateClientResponse,
} from "../api/clientManagement";
import type { CreateClientFormState } from "../types";

type ClientServiceField =
  | "learning_locker_enabled"
  | "occupational_health_enabled"
  | "wearables_enabled"
  | "akea_life_enabled";

interface ClientTabProps {
  effectiveClientId: string | null;
  isLoading: boolean;
  error: unknown;
  client: ClientDetails | null;
  isCentralAdmin: boolean;
  onToggleAnonymisation: (value: boolean) => void;
  onToggleClientService: (field: ClientServiceField, value: boolean) => void;
  anonymisationPending: boolean;
  createClientForm: CreateClientFormState;
  onCreateClientChange: (field: keyof CreateClientFormState, value: string | boolean) => void;
  onCreateClient: () => void;
  createClientPending: boolean;
  createClientResult: CreateClientResponse | null;
  onDismissCreateClientResult: () => void;
}

export const ClientTab: React.FC<ClientTabProps> = ({
  effectiveClientId,
  isLoading,
  error,
  client,
  isCentralAdmin,
  onToggleAnonymisation,
  onToggleClientService,
  anonymisationPending,
  createClientForm,
  onCreateClientChange,
  onCreateClient,
  createClientPending,
  createClientResult,
  onDismissCreateClientResult,
}) => {
  const hasResult = !!createClientResult;
  const hasMissingSeeds = createClientResult
    ? createClientResult.data.created_departments === 0 ||
      createClientResult.data.created_job_roles === 0
    : false;
  const [createModalOpen, setCreateModalOpen] = useState(false);

  useEffect(() => {
    if (hasResult) {
      setCreateModalOpen(false);
    }
  }, [hasResult]);

  const downloadQrCode = async () => {
    if (!client?.qrCodeUrl) return;
    const safeName = (client?.name || "client")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    const fileName = `${safeName || "client"}-qr.png`;

    try {
      const response = await fetch(client.qrCodeUrl);
      if (!response.ok) throw new Error("Failed to fetch QR code");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(client.qrCodeUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <SettingsSection
      title="Client"
      description="Review client details and QR code."
    >
      {isCentralAdmin && (
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={() => setCreateModalOpen(true)}
          >
            Create Client
          </Button>
        </Stack>
      )}

      {client && isCentralAdmin ? (
        <Card
          sx={{ border: "1px solid", borderColor: "divider", boxShadow: "none" }}
        >
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={600}>
                Privacy
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(
                      client.enable_anonymisation ?? client.enableAnonymisation
                    )}
                    onChange={(event) => onToggleAnonymisation(event.target.checked)}
                    disabled={anonymisationPending}
                  />
                }
                label="Enable anonymisation"
              />
              <Typography variant="body2" color="text.secondary">
                When enabled, filters and charts hide segments with fewer than two users.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {client && isCentralAdmin ? (
        <Card
          sx={{ border: "1px solid", borderColor: "divider", boxShadow: "none" }}
        >
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={600}>
                Enabled Services
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(
                      client.learning_locker_enabled ??
                        client.learningLockerEnabled
                    )}
                    onChange={(event) =>
                      onToggleClientService(
                        "learning_locker_enabled",
                        event.target.checked
                      )
                    }
                    disabled={anonymisationPending}
                  />
                }
                label="Learning Locker"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(
                      client.occupational_health_enabled ??
                        client.occupationalHealthEnabled
                    )}
                    onChange={(event) =>
                      onToggleClientService(
                        "occupational_health_enabled",
                        event.target.checked
                      )
                    }
                    disabled={anonymisationPending}
                  />
                }
                label="Occupational Health"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(
                      client.wearables_enabled ?? client.wearablesEnabled
                    )}
                    onChange={(event) =>
                      onToggleClientService(
                        "wearables_enabled",
                        event.target.checked
                      )
                    }
                    disabled={anonymisationPending}
                  />
                }
                label="Wearables"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(
                      client.akea_life_enabled ?? client.akeaLifeEnabled
                    )}
                    onChange={(event) =>
                      onToggleClientService(
                        "akea_life_enabled",
                        event.target.checked
                      )
                    }
                    disabled={anonymisationPending}
                  />
                }
                label="Akea Life"
              />
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {!effectiveClientId && (
        <Alert severity="info">Select a client to view details.</Alert>
      )}

      {Boolean(error) && (
        <Alert severity="error">
          Failed to load client details. Please try again.
        </Alert>
      )}

      {!effectiveClientId ? null : isLoading ? (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading client details...</Typography>
        </Box>
      ) : (
        <Card
          sx={{ border: "1px solid", borderColor: "divider", boxShadow: "none" }}
        >
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {client?.name ?? "Client"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status: {client?.is_active === false || client?.isActive === false ? "Inactive" : "Active"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Client QR Code
                </Typography>
                {client?.qrCodeUrl ? (
                  <Stack spacing={1} alignItems="flex-start">
                    <Box
                      component="img"
                      src={client.qrCodeUrl}
                      alt="Client QR code"
                      sx={{
                        width: 180,
                        height: 180,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={downloadQrCode}
                    >
                      Download QR
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {client?.qrCodeValue ?? "QR code value unavailable"}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    QR code not available yet. Update the client to regenerate it.
                  </Typography>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      <CreateClientToast
        open={!!createClientResult}
        result={createClientResult}
        onClose={onDismissCreateClientResult}
      />
      <Dialog
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create client</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Client name"
              value={createClientForm.name}
              onChange={(event) =>
                onCreateClientChange("name", event.target.value)
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={createClientForm.is_active}
                  onChange={(event) =>
                    onCreateClientChange("is_active", event.target.checked)
                  }
                />
              }
              label="Active"
            />
            <Typography variant="subtitle2">Enabled services</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={createClientForm.learning_locker_enabled}
                  onChange={(event) =>
                    onCreateClientChange(
                      "learning_locker_enabled",
                      event.target.checked
                    )
                  }
                />
              }
              label="Learning Locker"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={createClientForm.occupational_health_enabled}
                  onChange={(event) =>
                    onCreateClientChange(
                      "occupational_health_enabled",
                      event.target.checked
                    )
                  }
                />
              }
              label="Occupational Health"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={createClientForm.wearables_enabled}
                  onChange={(event) =>
                    onCreateClientChange(
                      "wearables_enabled",
                      event.target.checked
                    )
                  }
                />
              }
              label="Wearables"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={createClientForm.akea_life_enabled}
                  onChange={(event) =>
                    onCreateClientChange(
                      "akea_life_enabled",
                      event.target.checked
                    )
                  }
                />
              }
              label="Akea Life"
            />
            {hasResult && (
              <Alert severity={hasMissingSeeds ? "warning" : "success"}>
                Created {createClientResult?.data.created_departments} departments
                and {createClientResult?.data.created_job_roles} job roles.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!createClientForm.name.trim() || createClientPending}
            onClick={onCreateClient}
          >
            Create client
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsSection>
  );
};

const CreateClientToast: React.FC<{
  open: boolean;
  result: CreateClientResponse | null;
  onClose: () => void;
}> = ({ open, result, onClose }) => {
  const [isOpen, setIsOpen] = useState(open);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  if (!result) return null;

  const hasMissingSeeds =
    result.data.created_departments === 0 || result.data.created_job_roles === 0;

  return (
    <Snackbar
      open={isOpen}
      autoHideDuration={6000}
      onClose={() => {
        setIsOpen(false);
        onClose();
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      <Alert
        onClose={() => {
          setIsOpen(false);
          onClose();
        }}
        severity={hasMissingSeeds ? "warning" : "success"}
        variant="filled"
      >
        Client created with {result.data.created_departments} departments and{" "}
        {result.data.created_job_roles} job roles.
      </Alert>
    </Snackbar>
  );
};
